import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import ClientReadiness from "../components/training/ClientReadiness";
import {
  IdentificationCard, GraduationCap, HardHat, SteeringWheel, CheckCircle,
  CalendarBlank, Buildings, Briefcase, Phone, EnvelopeSimple, ShieldCheck,
  PencilSimple, Plus, UploadSimple, WarningCircle,
} from "@phosphor-icons/react";

async function uploadEvidence(file) {
  const fd = new FormData();
  fd.append("category", "competency");
  fd.append("file", file);
  const r = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data; // {id, name}
}

const fmtUK = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB");
};

const COMP_STATE = {
  current: { label: "Current", cls: "bg-emerald-100 text-emerald-700", edge: "border-l-emerald-500" },
  verified: { label: "Verified", cls: "bg-emerald-100 text-emerald-700", edge: "border-l-emerald-500" },
  expiring: { label: "Expiring soon", cls: "bg-amber-100 text-amber-700", edge: "border-l-amber-500" },
  expired: { label: "Expired", cls: "bg-red-100 text-red-700", edge: "border-l-red-500" },
  awaiting_verification: { label: "Awaiting verification", cls: "bg-blue-100 text-blue-700", edge: "border-l-blue-500" },
  more_info: { label: "More info required", cls: "bg-amber-100 text-amber-800", edge: "border-l-amber-500" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700", edge: "border-l-red-500" },
};

const LIC_STATE = {
  valid: "bg-emerald-100 text-emerald-700",
  expired: "bg-red-100 text-red-700",
  revoked: "bg-red-100 text-red-700",
  disqualified: "bg-red-100 text-red-700",
};

const Section = ({ Icon, title, count, children, testid }) => (
  <section data-testid={testid} className="bg-white border-2 border-slate-200 rounded-xl p-5 sm:p-6">
    <div className="flex items-center gap-2.5 mb-4">
      <Icon size={22} weight="bold" className="text-slate-700" />
      <h2 className="font-display text-lg font-black tracking-tight text-slate-900">{title}</h2>
      {count != null && <span className="ml-auto font-mono text-xs text-slate-400">{count}</span>}
    </div>
    {children}
  </section>
);

export default function MyProfilePage() {
  const { profile } = useAuth();
  const [summary, setSummary] = useState(null);
  const [licence, setLicence] = useState(null);
  const [comps, setComps] = useState([]);
  const [ppe, setPpe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ phone: "", emergency_contact_name: "", emergency_contact_relationship: "", emergency_contact_phone: "" });
  const [reqOpen, setReqOpen] = useState(false);
  const [reqForm, setReqForm] = useState({ competency_type_id: "", provider: "", certificate_number: "", achieved_date: "", expiry_date: "", no_expiry: false });
  const [reqFile, setReqFile] = useState(null);
  const [ctypes, setCtypes] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get("/me/summary");
      setSummary(s.data);
      setContactForm({
        phone: s.data.phone || "", emergency_contact_name: s.data.emergency_contact_name || "",
        emergency_contact_relationship: s.data.emergency_contact_relationship || "", emergency_contact_phone: s.data.emergency_contact_phone || "",
      });
      const pid = s.data.id;
      const [lic, cp, pp, ct] = await Promise.all([
        api.get(`/people/${pid}/licence`).catch(() => ({ data: null })),
        api.get(`/training/competencies?profile_id=${pid}`).catch(() => ({ data: [] })),
        api.get("/ppe/mine").catch(() => ({ data: [] })),
        api.get("/config/competency-types").catch(() => ({ data: [] })),
      ]);
      setLicence(lic.data);
      setComps(cp.data || []);
      setPpe(pp.data || []);
      setCtypes((ct.data || []).filter((t) => t.is_active !== false));
    } catch (e) {
      toast.error("Could not load your profile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveContact = async () => {
    setBusy(true);
    try { await api.put("/me/contact", contactForm); toast.success("Contact details updated"); setContactOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const submitRequest = async () => {
    if (!reqForm.competency_type_id) { toast.error("Choose a competency type"); return; }
    setBusy(true);
    try {
      let ev = {};
      if (reqFile) { const up = await uploadEvidence(reqFile); ev = { evidence_file_id: up.id, evidence_name: up.name, certificate_url: up.id }; }
      await api.post("/training/my/competencies", { ...reqForm, ...ev });
      toast.success("Submitted for verification");
      setReqOpen(false); setReqFile(null);
      setReqForm({ competency_type_id: "", provider: "", certificate_number: "", achieved_date: "", expiry_date: "", no_expiry: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const uploadToExisting = async (cid, file) => {
    if (!file) return;
    setBusy(true);
    try { const up = await uploadEvidence(file); await api.post(`/training/my/competencies/${cid}/evidence`, { evidence_file_id: up.id, evidence_name: up.name }); toast.success("Evidence submitted — awaiting verification"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const confirmPpe = async (id) => {
    try { await api.post(`/ppe/${id}/acknowledge`, {}); toast.success("Receipt confirmed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const p = summary || profile || {};
  const initials = `${(p.first_name || "")[0] || ""}${(p.last_name || "")[0] || ""}`.toUpperCase();
  const lic = licence?.licence;
  const ents = licence?.entitlements || [];

  if (loading && !summary) {
    return <div className="text-slate-500 py-10 text-center" data-testid="my-profile-loading">Loading your profile…</div>;
  }

  return (
    <div className="flex flex-col gap-5 max-w-4xl" data-testid="my-profile-page">
      {/* Identity header */}
      <div className="bg-slate-900 text-white rounded-xl p-6 flex items-center gap-5">
        <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center font-display text-2xl font-black shrink-0" data-testid="my-profile-avatar">{initials || "?"}</div>
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight truncate" data-testid="my-profile-name">{p.first_name} {p.last_name}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm text-slate-300">
            {p.job_title_name && <span className="inline-flex items-center gap-1.5"><Briefcase size={14} weight="bold" />{p.job_title_name}</span>}
            {p.department_name && <span className="inline-flex items-center gap-1.5"><Buildings size={14} weight="bold" />{p.department_name}</span>}
            {p.employee_number && <span className="font-mono text-slate-400">#{p.employee_number}</span>}
          </div>
        </div>
      </div>

      {/* Personal details */}
      <Section Icon={IdentificationCard} title="My Details" testid="my-profile-details">
        <div className="flex justify-end -mt-10 mb-2">
          <Button data-testid="edit-contact-btn" onClick={() => setContactOpen(true)} variant="outline" className="h-8 text-xs font-semibold"><PencilSimple size={14} className="mr-1" />Edit contact</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Detail Icon={EnvelopeSimple} label="Email" value={p.email} />
          <Detail Icon={Phone} label="Phone" value={p.phone} />
          <Detail Icon={CalendarBlank} label="Start date" value={fmtUK(p.start_date)} />
          <Detail Icon={ShieldCheck} label="Access" value={(p.permissions || []).length ? (p.permissions || []).join(", ") : "—"} />
          <Detail Icon={Phone} label="Emergency contact" value={p.emergency_contact_name ? `${p.emergency_contact_name}${p.emergency_contact_relationship ? ` (${p.emergency_contact_relationship})` : ""}${p.emergency_contact_phone ? ` · ${p.emergency_contact_phone}` : ""}` : "—"} />
          {(p.team_memberships || []).length > 0 && (
            <Detail Icon={Buildings} label="Teams" value={p.team_memberships.map((m) => m.team_name + (m.team_role_name ? ` (${m.team_role_name})` : "")).join(", ")} />
          )}
        </div>
      </Section>

      {/* Driving licence */}
      <Section Icon={SteeringWheel} title="Driving Licence" testid="my-profile-licence">
        {lic ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase ${LIC_STATE[lic.status] || "bg-slate-100 text-slate-600"}`} data-testid="my-licence-status">{lic.status || "unknown"}</span>
              {lic.licence_type && <span className="text-sm text-slate-600 capitalize">{lic.licence_type}</span>}
              {lic.licence_number && <span className="font-mono text-sm text-slate-500">{lic.licence_number}</span>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              <Detail label="Expires" value={fmtUK(lic.expiry_date)} />
              <Detail label="Last checked" value={fmtUK(lic.check_date)} />
              <Detail label="Next check due" value={fmtUK(lic.next_check_due)} />
            </div>
            {ents.length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Categories held</div>
                <div className="flex flex-wrap gap-2" data-testid="my-licence-entitlements">
                  {ents.map((e) => (
                    <span key={e.id} className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-1 text-sm">
                      <span className="font-mono font-bold text-slate-800">{e.category_code}</span>
                      {!e.full && <span className="text-[10px] font-bold text-amber-600 uppercase">Prov</span>}
                      {e.held_duration && <span className="text-xs text-slate-500">· {e.held_duration}</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-slate-400 text-sm" data-testid="my-licence-none">No driving licence recorded. Ask your manager to add one if you drive company vehicles.</p>
        )}
      </Section>

      {/* Competencies & authorisations */}
      <Section Icon={GraduationCap} title="Training & Competencies" count={comps.length} testid="my-profile-competencies">
        <div className="flex justify-end -mt-10 mb-2">
          <Button data-testid="request-competency-btn" onClick={() => setReqOpen(true)} className="h-8 text-xs font-semibold bg-slate-900 hover:bg-slate-800"><Plus size={14} className="mr-1" />Add / request</Button>
        </div>
        {comps.length ? (
          <div className="flex flex-col gap-2">
            {comps.map((c) => {
              const st = COMP_STATE[c.state] || { label: c.state, cls: "bg-slate-100 text-slate-600", edge: "border-l-slate-300" };
              return (
                <div key={c.id} data-testid={`my-comp-${c.id}`} className={`bg-white border-2 border-slate-200 border-l-8 ${st.edge} rounded-lg p-3.5 flex flex-col gap-2`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-body font-semibold text-slate-900 truncate">{c.competency_name || "Competency"}</div>
                      <div className="font-mono text-xs text-slate-500">
                        {c.provider ? `${c.provider} · ` : ""}{c.no_expiry ? "No expiry" : (c.expiry_date ? `Expires ${fmtUK(c.expiry_date)}` : "No expiry date")}
                      </div>
                    </div>
                    <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-bold uppercase ${st.cls}`}>{st.label}</span>
                  </div>
                  {(c.state === "rejected" || c.state === "more_info") && c.review_notes && (
                    <div className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                      <WarningCircle size={14} weight="bold" className="mt-0.5 shrink-0" /><span>Reviewer: {c.review_notes}</span>
                    </div>
                  )}
                  <label className="text-xs font-semibold text-blue-700 inline-flex items-center gap-1 cursor-pointer w-fit" data-testid={`my-comp-upload-${c.id}`}>
                    <UploadSimple size={14} weight="bold" />{c.certificate_url ? "Replace certificate" : "Upload certificate"}
                    <input type="file" accept="image/*,application/pdf" className="hidden" disabled={busy} onChange={(e) => uploadToExisting(c.id, e.target.files?.[0])} />
                  </label>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-slate-400 text-sm" data-testid="my-comp-none">No training records yet — use “Add / request” to submit one.</p>
        )}
      </Section>

      {/* Client readiness */}
      {p.id && <ClientReadiness profileId={p.id} />}

      {/* PPE */}
      <Section Icon={HardHat} title="My PPE" count={ppe.length} testid="my-profile-ppe">
        {ppe.length ? (
          <div className="flex flex-col gap-2">
            {ppe.map((i) => (
              <div key={i.id} data-testid={`my-ppe-${i.id}`} className={`bg-white border-2 rounded-lg p-3.5 flex items-center justify-between gap-3 border-l-8 ${i.employee_ack_at ? "border-l-emerald-500 border-slate-200" : "border-l-amber-500 border-amber-200"}`}>
                <div className="min-w-0">
                  <div className="font-body font-semibold text-slate-900 truncate">{i.ppe_name} {i.size ? `· ${i.size}` : ""}</div>
                  <div className="font-mono text-xs text-slate-500">Qty {i.quantity}{i.serial_number ? ` · SN ${i.serial_number}` : ""}{i.replacement_due ? ` · replace ${fmtUK(i.replacement_due)}` : ""}</div>
                </div>
                {i.employee_ack_at
                  ? <span className="shrink-0 inline-flex items-center gap-1 text-xs font-bold uppercase text-emerald-600"><CheckCircle size={16} weight="fill" />Confirmed</span>
                  : <Button data-testid={`my-ppe-confirm-${i.id}`} onClick={() => confirmPpe(i.id)} className="shrink-0 bg-emerald-600 hover:bg-emerald-700 font-semibold h-9">Confirm Receipt</Button>}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-400 text-sm" data-testid="my-ppe-none">No PPE issued to you.</p>
        )}
      </Section>

      {/* Edit contact dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Edit contact details</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">These apply immediately and your manager is notified. Formal HR details are managed by your admin.</p>
          <label className="text-xs font-semibold text-slate-600">Phone</label>
          <Input data-testid="contact-phone" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
          <label className="text-xs font-semibold text-slate-600">Emergency contact name</label>
          <Input data-testid="contact-ec-name" value={contactForm.emergency_contact_name} onChange={(e) => setContactForm({ ...contactForm, emergency_contact_name: e.target.value })} />
          <label className="text-xs font-semibold text-slate-600">Relationship</label>
          <Input data-testid="contact-ec-rel" value={contactForm.emergency_contact_relationship} onChange={(e) => setContactForm({ ...contactForm, emergency_contact_relationship: e.target.value })} />
          <label className="text-xs font-semibold text-slate-600">Emergency contact phone</label>
          <Input data-testid="contact-ec-phone" value={contactForm.emergency_contact_phone} onChange={(e) => setContactForm({ ...contactForm, emergency_contact_phone: e.target.value })} />
          <Button data-testid="contact-save" onClick={saveContact} disabled={busy} className="bg-blue-600 hover:bg-blue-700 font-semibold">Save</Button>
        </DialogContent>
      </Dialog>

      {/* Request / add competency dialog */}
      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Add / request a competency</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">This is submitted for verification. It won’t count or authorise you until a Training Manager verifies it.</p>
          <label className="text-xs font-semibold text-slate-600">Competency type</label>
          <select data-testid="req-type" className="h-10 border-2 border-slate-300 rounded px-2" value={reqForm.competency_type_id} onChange={(e) => setReqForm({ ...reqForm, competency_type_id: e.target.value })}>
            <option value="">Choose…</option>{ctypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <label className="text-xs font-semibold text-slate-600">Provider (optional)</label>
          <Input data-testid="req-provider" value={reqForm.provider} onChange={(e) => setReqForm({ ...reqForm, provider: e.target.value })} />
          <label className="text-xs font-semibold text-slate-600">Certificate / reference number</label>
          <Input data-testid="req-certno" value={reqForm.certificate_number} onChange={(e) => setReqForm({ ...reqForm, certificate_number: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600">Issue date</label><Input data-testid="req-issue" type="date" value={reqForm.achieved_date} onChange={(e) => setReqForm({ ...reqForm, achieved_date: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-slate-600">Expiry date</label><Input data-testid="req-expiry" type="date" disabled={reqForm.no_expiry} value={reqForm.expiry_date} onChange={(e) => setReqForm({ ...reqForm, expiry_date: e.target.value })} /></div>
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600"><input type="checkbox" data-testid="req-noexpiry" checked={reqForm.no_expiry} onChange={(e) => setReqForm({ ...reqForm, no_expiry: e.target.checked })} />No expiry</label>
          <label className="text-xs font-semibold text-slate-600">Certificate / evidence photo</label>
          <input data-testid="req-file" type="file" accept="image/*,application/pdf" onChange={(e) => setReqFile(e.target.files?.[0] || null)} className="text-sm" />
          <Button data-testid="req-submit" onClick={submitRequest} disabled={busy || !reqForm.competency_type_id} className="bg-blue-600 hover:bg-blue-700 font-semibold">Submit for verification</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Detail = ({ Icon, label, value }) => (
  <div className="flex items-start gap-2">
    {Icon && <Icon size={16} weight="bold" className="text-slate-400 mt-0.5 shrink-0" />}
    <div className="min-w-0">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-slate-800 break-words">{value || "—"}</div>
    </div>
  </div>
);
